<script setup>
import {
  provide,
  toRef,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useResizableEditor } from 'dashboard/composables/useResizableEditor';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  collapsible: { type: Boolean, default: false },
  containerHeight: { type: Number, default: 0 },
  editorSelector: { type: String, default: '.ProseMirror-woot-style' },
  resetOnMessageSent: { type: Boolean, default: true },
});

const emit = defineEmits(['collapse']);
const { t } = useI18n();
const wrapperRef = useTemplateRef('wrapperRef');
const {
  appliedHeight,
  requestEditorHeight,
  sizeBounds,
  isExpanded,
  isResizing,
  onResizeStart,
  resizeBy,
  resetEditorHeight,
  toggleEditorExpand,
} = useResizableEditor({
  containerHeight: toRef(props, 'containerHeight'),
  editorSelector: toRef(props, 'editorSelector'),
  wrapperRef,
  collapsible: toRef(props, 'collapsible'),
  onCollapse: () => emit('collapse'),
});

provide('requestEditorHeight', requestEditorHeight);

let resetTimeoutId = null;
const handleMessageSent = () => {
  clearTimeout(resetTimeoutId);
  resetTimeoutId = setTimeout(resetEditorHeight, 120);
};
onMounted(() => {
  if (props.resetOnMessageSent)
    emitter.on(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.MESSAGE_SENT, handleMessageSent);
  clearTimeout(resetTimeoutId);
});

defineExpose({ toggleEditorExpand, resetEditorHeight, isExpanded });
</script>

<template>
  <div
    ref="wrapperRef"
    class="relative resizable-editor-wrapper"
    :style="{
      '--editor-height': appliedHeight + 'px',
      '--editor-min-allowed': sizeBounds.min + 'px',
      '--editor-max-allowed': sizeBounds.max + 'px',
      '--editor-height-transition': isResizing ? 'none' : '180ms ease',
    }"
  >
    <div
      class="group absolute inset-x-0 -top-4 z-10 flex h-4 cursor-row-resize select-none items-center justify-center bg-gradient-to-b from-transparent from-10% dark:to-n-surface-1/80 to-n-surface-1/90 backdrop-blur-[0.01875rem]"
      role="separator"
      tabindex="0"
      aria-orientation="horizontal"
      :aria-label="t('CONVERSATION.REPLYBOX.RESIZE_EDITOR')"
      :aria-valuemin="sizeBounds.min"
      :aria-valuemax="sizeBounds.max"
      :aria-valuenow="appliedHeight"
      @keydown.up.prevent="resizeBy(16)"
      @keydown.down.prevent="resizeBy(-16)"
      @keydown.enter.prevent="toggleEditorExpand"
      @mousedown="onResizeStart"
      @touchstart.prevent="onResizeStart"
      @dblclick="resetEditorHeight"
    >
      <div
        class="w-8 h-0.5 mt-1 rounded-full bg-n-slate-6 group-hover:bg-n-slate-8 transition-all duration-200 motion-safe:group-hover:animate-bounce"
        :class="{ 'bg-n-slate-8 animate-bounce': isResizing }"
      />
    </div>
    <slot />
  </div>
</template>
